import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { AdminShell } from "@project/admin/components/admin-shell";
import { StatCard, SectionCard } from "@project/admin/components/module-ui";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import {
  Users,
  ArrowRight,
  UtensilsCrossed,
  Sparkles,
  ClipboardList,
} from "lucide-react";
import { tiffinSevaAdminService } from "@project/services/admin/tiffin-seva.service";
import { projectToast } from "@project/lib/toast";

export default function TiffinSevaDashboard() {
  const [dashboardData, setDashboardData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    tiffinSevaAdminService
      .getDashboard()
      .then((res) => {
        if (res) {
          setDashboardData(res);
        }
      })
      .catch(() => {
        projectToast.error("Failed to load dashboard data.");
      })
      .finally(() => setLoading(false));
  }, []);

  const overview = dashboardData?.overview || { total_entries: 0, online_entries: 0, offline_entries: 0 };
  const categories = dashboardData?.categories || { above_60_years: 0, below_60_years: 0 };
  const payment = dashboardData?.payment || { not_required: 0, pending: 0, paid: 0, failed: 0, total_amount: "0.00" };
  const recentEntries = dashboardData?.recent_entries || [];

  const total = overview.total_entries;
  const paidCount = categories.below_60_years;
  const freeCount = categories.above_60_years;
  const successfulPayments = payment.paid;

  return (
    <AdminShell
      title="Tiffin Seva Dashboard"
      subtitle="Comprehensive overview of tiffin service inquiries, registrations, fees and analytics"
      actions={
        <Button asChild size="sm" variant="outline">
          <Link to="/admin/tiffin-seva/applications">
            Inquiries & Applications <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Link>
        </Button>
      }
    >
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="TOTAL APPLICATIONS"
          value={loading ? "..." : total}
          icon={<ClipboardList className="h-5 w-5" />}
          accent="from-primary/90 to-primary"
          sub="Submitted applications"
        />
        <StatCard
          label="PAID SEVA REGISTRATIONS"
          value={loading ? "..." : paidCount}
          icon={<UtensilsCrossed className="h-5 w-5" />}
          accent="from-blue-600 to-blue-500"
          sub={`${successfulPayments} verified payments`}
        />
        <StatCard
          label="FREE SEVA (SENIORS)"
          value={loading ? "..." : freeCount}
          icon={<Users className="h-5 w-5" />}
          accent="from-emerald-600 to-emerald-500"
          sub="Directly trust-sponsored"
        />
        <StatCard
          label="COMPLETED PAYMENTS"
          value={loading ? "..." : successfulPayments}
          icon={<Sparkles className="h-5 w-5" />}
          accent="from-amber-600 to-amber-500"
          sub={`Out of ${paidCount} paid registrations`}
        />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        {/* Seva distribution */}
        <SectionCard
          title="Seva Distribution"
          subtitle="Proportion of Paid Seva vs Free Seva"
          className="lg:col-span-1 text-foreground"
        >
          <div className="space-y-3 pt-2">
            {[
              { label: "Free Seva (Seniors)", value: freeCount, color: "bg-emerald-500" },
              { label: "Paid Seva (General)", value: paidCount, color: "bg-blue-500" },
            ].map(row => {
              const pct = total ? Math.round((row.value / total) * 100) : 0;
              return (
                <div key={row.label}>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="font-medium text-foreground">
                      {row.label}
                    </span>
                    <span className="text-muted-foreground">
                      {row.value} ({pct}%)
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={`h-full ${row.color}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>

        {/* Recent registration records */}
        <SectionCard
          title="Recent Applications"
          subtitle="Latest tiffin registration requests received"
          className="lg:col-span-2 text-foreground"
        >
          {loading ? (
            <p className="text-center text-xs text-muted-foreground py-10">Loading...</p>
          ) : recentEntries.length === 0 ? (
            <p className="text-center text-xs text-muted-foreground py-10">No applications registered yet.</p>
          ) : (
            <div className="divide-y divide-border">
              {recentEntries.slice(0, 5).map((item: any, idx: number) => (
                <div key={idx} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground font-serif">
                      {item.name}
                    </div>
                    <div className="truncate text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5 font-sans">
                      <span>Age: {item.age}</span>
                      <span>•</span>
                      <span>Start: {item.tiffin_start_date}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] uppercase font-semibold text-muted-foreground border-border/80">
                      {item.seva_category}
                    </Badge>
                    <Badge
                      className={
                        item.payment_status === "PAID"
                          ? "bg-emerald-600/10 text-emerald-600 font-bold border border-emerald-500/20 shadow-none text-[10px]"
                          : item.payment_status === "NOT_REQUIRED" || item.payment_status === "FREE"
                          ? "bg-blue-600/10 text-blue-600 font-bold border border-blue-500/20 shadow-none text-[10px]"
                          : "bg-amber-600/10 text-amber-600 font-bold border border-amber-500/20 shadow-none text-[10px]"
                      }
                    >
                      {item.payment_status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </AdminShell>
  );
}
